import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import type { Achievement } from '../../types/buildExpansion';
import AchievementBadge, { formatRankContext, formatScopeLabel } from './AchievementBadge';
import '../badges/BadgeStack.css';

const MILESTONE_PREVIEW_COUNT = 3;

const TIER_ORDER: Record<Achievement['visual_tier'], number> = {
  exceptional: 0,
  notable: 1,
  standard: 2,
};

function sortMilestones(list: Achievement[]): Achievement[] {
  return [...list].sort((a, b) => {
    const tierDelta = (TIER_ORDER[a.visual_tier] ?? 9) - (TIER_ORDER[b.visual_tier] ?? 9);
    if (tierDelta !== 0) return tierDelta;
    const da = a.issued_at || a.effective_at || '';
    const db = b.issued_at || b.effective_at || '';
    return db.localeCompare(da);
  });
}

function milestoneMonogram(achievement: Achievement): string {
  const name = (achievement.short_name || achievement.name || '?').trim();
  const match = name.match(/[A-Za-z0-9]/);
  return (match ? match[0] : '?').toUpperCase();
}

function milestoneDiscClass(tier: Achievement['visual_tier']): string {
  if (tier === 'exceptional') {
    return 'border-orange-500/45 bg-orange-950/70 text-orange-200';
  }
  if (tier === 'notable') {
    return 'border-white/25 bg-[#1f1f1f] text-white';
  }
  return 'border-white/15 bg-[#171717] text-zinc-300';
}

/**
 * Groups a build's recognition with strict class separation: platform
 * achievements (Bilt:Spec calculated) and milestones never share a section.
 * Historical entries collapse behind a disclosure. Selecting a badge opens
 * an inline detail panel.
 */

const formatDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const humanizeToken = (token: string): string => token.replace(/[_-]+/g, ' ').trim();

interface AchievementDetailProps {
  achievement: Achievement;
  onClose: () => void;
}

const AchievementDetail: React.FC<AchievementDetailProps> = ({ achievement, onClose }) => {
  const name = achievement.name || achievement.short_name || 'Achievement';
  const rankContext =
    achievement.achievement_class === 'platform_achievement' ? formatRankContext(achievement) : null;
  const scopeLabel = formatScopeLabel(achievement.scope);
  const issued = formatDate(achievement.issued_at);
  const valueSnapshot =
    achievement.value != null
      ? `${achievement.value.toLocaleString()}${achievement.metric ? ` ${humanizeToken(achievement.metric)}` : ''}`
      : null;

  return (
    <div
      role="region"
      aria-label={`${name} details`}
      className="rounded-xl border border-white/[0.08] bg-[#0a0a0a]/80 p-4 mt-3"
    >
      <div className="flex items-start justify-between gap-3">
        <h4 className="text-sm font-semibold text-white">{name}</h4>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close achievement details"
          className="btn-icon shrink-0 -mr-1 -mt-1 cursor-pointer"
        >
          <XMarkIcon className="w-4 h-4" aria-hidden />
        </button>
      </div>

      {achievement.description && (
        <p className="text-sm text-gray-400 mt-1.5 leading-relaxed">{achievement.description}</p>
      )}

      <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {rankContext && (
          <div>
            <dt className="text-meta">Standing</dt>
            <dd className="text-gray-200 mt-0.5">{rankContext}</dd>
          </div>
        )}
        {valueSnapshot && (
          <div>
            <dt className="text-meta">Value at snapshot</dt>
            <dd className="text-gray-200 mt-0.5 tabular-nums">{valueSnapshot}</dd>
          </div>
        )}
        {issued && (
          <div>
            <dt className="text-meta">Issued</dt>
            <dd className="text-gray-200 mt-0.5">{issued}</dd>
          </div>
        )}
        {scopeLabel && (
          <div>
            <dt className="text-meta">Scope</dt>
            <dd className="text-gray-200 mt-0.5">{scopeLabel}</dd>
          </div>
        )}
      </dl>
    </div>
  );
};

interface BadgeGroupProps {
  heading: string;
  description?: string;
  achievements: Achievement[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onCloseDetail: () => void;
  /** When set, show this many full cards then an overlapping disc stack + See more. */
  collapseAfter?: number;
}

const BadgeGroup: React.FC<BadgeGroupProps> = ({
  heading,
  description,
  achievements,
  selectedId,
  onSelect,
  onCloseDetail,
  collapseAfter,
}) => {
  const [expanded, setExpanded] = useState(false);
  if (achievements.length === 0) return null;

  const shouldCollapse = collapseAfter != null && achievements.length > collapseAfter;
  const featured = shouldCollapse ? achievements.slice(0, collapseAfter) : achievements;
  const remaining = shouldCollapse ? achievements.slice(collapseAfter) : [];
  const visible = shouldCollapse && !expanded ? featured : achievements;
  const selected = visible.find((a) => a.id === selectedId) || null;
  const hiddenCount = remaining.length;

  return (
    <div>
      <div className="flex items-baseline gap-3">
        <h3 className="font-display text-base font-semibold tracking-tight text-white">{heading}</h3>
        <span className="font-mono text-[11px] text-gray-600 tabular-nums" aria-hidden>
          {String(achievements.length).padStart(2, '0')}
        </span>
      </div>
      {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-3 list-none">
        {visible.map((achievement) => (
          <li key={achievement.id}>
            <AchievementBadge
              achievement={achievement}
              expanded={achievement.id === selectedId}
              onClick={() =>
                achievement.id === selectedId ? onCloseDetail() : onSelect(achievement.id)
              }
            />
          </li>
        ))}
      </ul>
      {selected && <AchievementDetail achievement={selected} onClose={onCloseDetail} />}

      {shouldCollapse && !expanded ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div
            className="badge-stack-overlap"
            aria-label={`${hiddenCount} more milestone${hiddenCount === 1 ? '' : 's'}`}
          >
            {remaining.slice(0, 8).map((achievement, index) => {
              const label = achievement.short_name || achievement.name || 'Milestone';
              return (
                <div
                  key={achievement.id}
                  className="badge-stack-overlap__item"
                  style={{ zIndex: remaining.length - index }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setExpanded(true);
                      onSelect(achievement.id);
                    }}
                    title={label}
                    aria-label={`Show more milestones, starting with ${label}`}
                    className={`h-[26px] w-[26px] rounded-full border text-[10px] font-semibold tabular-nums flex items-center justify-center cursor-pointer shadow-sm shadow-black/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 ${milestoneDiscClass(
                      achievement.visual_tier
                    )}`}
                  >
                    {milestoneMonogram(achievement)}
                  </button>
                </div>
              );
            })}
            {remaining.length > 8 ? (
              <button
                type="button"
                className="badge-stack-overlap__more"
                onClick={() => setExpanded(true)}
                aria-label={`${remaining.length - 8} more milestones`}
              >
                +{remaining.length - 8}
              </button>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            aria-expanded={false}
            className="text-sm font-medium text-orange-300 hover:text-orange-200 transition-colors duration-200 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400 rounded min-h-[40px]"
          >
            See {hiddenCount} more
          </button>
        </div>
      ) : null}

      {shouldCollapse && expanded ? (
        <button
          type="button"
          onClick={() => {
            setExpanded(false);
            if (selectedId != null && remaining.some((a) => a.id === selectedId)) {
              onCloseDetail();
            }
          }}
          aria-expanded
          className="mt-3 text-sm font-medium text-zinc-400 hover:text-white transition-colors duration-200 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400 rounded min-h-[40px]"
        >
          Show less
        </button>
      ) : null}
    </div>
  );
};

export interface AchievementCollectionProps {
  achievements: Achievement[];
  className?: string;
  /** Hide the section kicker/title when nested under Recognition. */
  hideIntro?: boolean;
  /** Limit which achievement classes are shown. */
  focus?: 'all' | 'achievements' | 'milestones';
  compact?: boolean;
}

export const AchievementCollection: React.FC<AchievementCollectionProps> = ({
  achievements,
  className = '',
  hideIntro = false,
  focus = 'all',
  compact = false,
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [historicalOpen, setHistoricalOpen] = useState(false);

  const groups = useMemo(() => {
    const current = achievements.filter((a) => a.status === 'current' || a.status == null);
    const historical = achievements.filter((a) => a.status != null && a.status !== 'current');
    const platform = current.filter((a) => a.achievement_class === 'platform_achievement');
    const credentials = current.filter((a) => a.achievement_class === 'verified_credential');
    const milestones = sortMilestones(
      current.filter((a) => a.achievement_class === 'build_milestone')
    );
    const other = current.filter((a) => a.achievement_class == null);
    return {
      platform: focus === 'milestones' ? [] : platform,
      credentials: focus === 'milestones' ? [] : credentials,
      milestones: focus === 'achievements' ? [] : milestones,
      other: focus === 'milestones' ? [] : other,
      historical:
        focus === 'milestones'
          ? historical.filter((a) => a.achievement_class === 'build_milestone')
          : focus === 'achievements'
            ? historical.filter((a) => a.achievement_class !== 'build_milestone')
            : historical,
    };
  }, [achievements, focus]);

  if (achievements.length === 0) return null;

  const closeDetail = () => setSelectedId(null);
  const hasVisible =
    groups.platform.length > 0 ||
    groups.credentials.length > 0 ||
    groups.milestones.length > 0 ||
    groups.other.length > 0 ||
    groups.historical.length > 0;
  if (!hasVisible) return null;

  return (
    <section
      className={`${compact ? 'space-y-4' : 'border border-white/[0.06] bg-[#0c0c0c]/80 p-5 sm:p-8 space-y-8'} ${className}`}
      aria-label={
        focus === 'milestones'
          ? 'Build milestones'
          : focus === 'achievements'
            ? 'Platform achievements'
            : 'Build recognition'
      }
    >
      {!hideIntro ? (
        <div className="border-b border-white/[0.06] pb-5">
          <p className="kicker mb-2">Achievements</p>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">
            What this build has earned
          </h2>
          <p className="text-sm text-gray-500 mt-2 max-w-[48ch] leading-relaxed">
            Platform standings, verified credentials, and milestones from what is documented here.
          </p>
        </div>
      ) : focus === 'all' && !compact ? (
        <div className="pb-1">
          <h3 className="spec-label">Platform achievements & milestones</h3>
        </div>
      ) : null}
      <BadgeGroup
        heading="Bilt:Spec achievements"
        description="Earned from what’s documented on this build."
        achievements={groups.platform}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onCloseDetail={closeDetail}
      />
      <BadgeGroup
        heading="Verified credentials"
        achievements={groups.credentials}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onCloseDetail={closeDetail}
      />
      <BadgeGroup
        heading="Milestones"
        description="Documented moments in this build's history."
        achievements={groups.milestones}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onCloseDetail={closeDetail}
        collapseAfter={MILESTONE_PREVIEW_COUNT}
      />
      <BadgeGroup
        heading="Other recognition"
        achievements={groups.other}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onCloseDetail={closeDetail}
      />

      {groups.historical.length > 0 && (
        <div className="border-t border-white/[0.06] pt-4">
          <button
            type="button"
            onClick={() => setHistoricalOpen((open) => !open)}
            aria-expanded={historicalOpen}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-300 hover:text-white transition-colors duration-200 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400 rounded min-h-[44px] md:min-h-[40px]"
          >
            <ChevronDownIcon
              className={`w-4 h-4 transition-transform duration-200 motion-reduce:transition-none ${
                historicalOpen ? 'rotate-180' : ''
              }`}
              aria-hidden
            />
            Historical ({groups.historical.length})
          </button>
          {historicalOpen && (
            <div className="mt-1">
              <p className="text-xs text-gray-500">
                Recognition this build previously held. Standings change as the platform grows.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-3 list-none">
                {groups.historical.map((achievement) => (
                  <li key={achievement.id}>
                    <AchievementBadge
                      achievement={achievement}
                      expanded={achievement.id === selectedId}
                      onClick={() =>
                        achievement.id === selectedId ? closeDetail() : setSelectedId(achievement.id)
                      }
                    />
                  </li>
                ))}
              </ul>
              {(() => {
                const selected = groups.historical.find((a) => a.id === selectedId) || null;
                return selected ? (
                  <AchievementDetail achievement={selected} onClose={closeDetail} />
                ) : null;
              })()}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default AchievementCollection;
